// AI-CONTEXT: See docs/test-runs/2026-10-20-rapportage-per-deelnemer.md (B4)
import { useEffect, useRef, useState } from 'react';
import Modal from '../../../common/Modal';

/**
 * Vraagt de titel van een nieuwe rapportage vóór er iets aangemaakt wordt.
 * Nodig omdat de plusknop meteen een rapport 'Live rapportage <datum>'
 * aanmaakte, niet te onderscheiden van een kopie of een vergeten proefje.
 *
 * De aanroeper rendert dit venster alleen als het open is, dus de titel
 * begint bij elke opening leeg en annuleren laat niets achter.
 *
 * @param {object} props
 * @param {(open: boolean) => void} props.setModalOpen - Sluit het venster (met `false`)
 * @param {(name: string) => Promise<unknown>} props.onCreate - Maakt het rapport aan met de getrimde titel
 * @returns {JSX.Element}
 */
const NewReportModal = ({ setModalOpen, onCreate }) => {
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(/** @type {string|null} */ (null));

    /* Een ref, geen state: twee klikken in dezelfde tik zien de state nog
       allebei op `false` staan en maken anders twee rapporten. */
    const busyRef = useRef(false);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const trimmed = title.trim();

    const create = async () => {
        if (!trimmed || busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setError(null);
        try {
            await onCreate(trimmed);
            if (isMounted.current) setModalOpen(false);
        } catch (e) {
            console.error('NewReportModal: create failed', e);
            if (isMounted.current) setError('De rapportage kon niet aangemaakt worden. Probeer het opnieuw.');
        } finally {
            busyRef.current = false;
            if (isMounted.current) setBusy(false);
        }
    };

    return (
        <Modal
            modalOpen={true}
            setModalOpen={setModalOpen}
            pageHeader="Nieuwe rapportage"
            confirm={create}
            confirmDisabled={!trimmed || busy}
            confirmLabel="Aanmaken"
            content={
                <div className="new-report-modal">
                    <label htmlFor="new-report-title">Titel</label>
                    <input
                        id="new-report-title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                        placeholder="Bijvoorbeeld: Verantwoording gemeente 2026"
                        autoFocus
                    />
                    {busy && <p>Bezig met aanmaken…</p>}
                    {error && <p className="share-report-error">{error}</p>}
                </div>
            }
        />
    );
};

export default NewReportModal;
